'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useSupabaseAuth } from './SupabaseAuthContext';
import { usePathname } from 'next/navigation';

// List of paths that don't require authentication
const PUBLIC_PATHS = ['/', '/login', '/home', '/fallback'];

interface AuthLoaderProps {
  children: ReactNode;
}

/**
 * AuthLoader component with timeout circuit breaker
 * Provides loading states and handles auth initialization
 */
export default function AuthLoader({ children }: AuthLoaderProps) {
  const { status, isLoading, error } = useSupabaseAuth();
  const [showFallback, setShowFallback] = useState(false);
  const pathname = usePathname();
  
  // Determine if the current path is public
  const isPublicPath = PUBLIC_PATHS.includes(pathname || '') || 
                     pathname?.startsWith('/api/') || 
                     pathname?.includes('/webhooks/');
  
  // Set a timeout for showing fallback UI
  useEffect(() => {
    // Don't show fallback for public routes
    if (isPublicPath) return;
    
    // Set timeout for showing fallback UI (5 seconds)
    const timeout = setTimeout(() => {
      if (isLoading || status === 'loading') {
        setShowFallback(true);
      }
    }, 5000);
    
    // Clear timeout when component unmounts or auth state changes
    return () => clearTimeout(timeout);
  }, [isLoading, status, isPublicPath]);
  
  // Allow immediate rendering for public paths
  if (isPublicPath) {
    return <>{children}</>;
  }
  
  // Show fallback UI if initialization is taking too long
  if (showFallback) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md w-full p-8 bg-card border rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-foreground">Authentication Taking Too Long</h2>
          <p className="text-muted-foreground mb-6">
            We're having trouble initializing your authentication. You can:
          </p>
          
          <div className="flex flex-col space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Reload Page
            </button>
            
            <button
              onClick={() => window.location.href = '/api/auth/reset-session?redirectTo=/login'}
              className="py-2 px-4 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90"
            >
              Reset Session
            </button>
            
            <button
              onClick={() => window.location.href = '/login'}
              className="py-2 px-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
            >
              Go to Login
            </button>
          </div>
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 rounded text-xs text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Show loading UI if still initializing
  if (isLoading || status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center">
          <div className="h-8 w-8 rounded-full border-4 border-primary border-r-transparent animate-spin mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    );
  }
  
  // Authentication is initialized, render children
  return <>{children}</>;
}